import React, { useState, useContext, useEffect } from 'react';
import { CartContext } from './CartContext';
import { Link, useLocation } from 'react-router-dom';
import { FaExpand, FaShoppingCart, FaEye, FaWeightHanging, FaArrowUp, FaTimes, FaStar } from 'react-icons/fa';
import '../css/ProductList.css';

const ProductList = ({ products }) => {
    const { addToCart } = useContext(CartContext);
    const [selectedWeights, setSelectedWeights] = useState({});
    const [expandedImage, setExpandedImage] = useState(null);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [recentlyAdded, setRecentlyAdded] = useState([]);
    const location = useLocation();

    // Handle scroll to top button visibility
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 400);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to top function
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Scroll to products section if hash is present
    useEffect(() => {
        if (location.hash === '#products-section') {
            const productsSection = document.getElementById('products-section');
            if (productsSection) {
                setTimeout(() => {
                    productsSection.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }, 100);
            }
        }
    }, [location]);

    // Initialize default weights
    useEffect(() => {
        const initialWeights = {};

        products.forEach(product => {
            // Set default weight based on category
            if (product.category === 'flower' || product.category === 'TopShelf flower' || product.category === 'exotic') {
                initialWeights[product.id] = 'ounce';
            }
        });

        setSelectedWeights(initialWeights);
    }, [products]);

    const handleWeightChange = (productId, weight) => {
        setSelectedWeights(prev => ({
            ...prev,
            [productId]: weight
        }));
    };

    const handleAddToCart = (product) => {
        if (product.countInStock > 0) {
            const isFlower = product.category === 'flower';
            const isTopShelf = product.category === 'TopShelf flower';
            const isExotic = product.category === 'exotic';
            const selectedWeight = selectedWeights[product.id] || 'ounce';

            // Define price options for each category
            const priceOptions = isTopShelf ? {
                ounce: product.price,
                half: 70,
                quarter: 50,
                eighth: 40
            } : isExotic ? {
                ounce: product.price,
                half: 100,
                quarter: 70,
                eighth: 50
            } : isFlower ? {
                ounce: product.price,
                half: product.price * 0.6,
                quarter: product.price * 0.4,
                eighth: product.price * 0.3
            } : { default: product.price };

            const itemPrice = (isFlower || isTopShelf || isExotic) ? priceOptions[selectedWeight] : product.price;

            const cartItem = {
                id: `${product.id}-${selectedWeight}-${Date.now()}`,
                productId: product.id,
                name: product.name,
                image: product.image,
                price: itemPrice,
                ...((isFlower || isTopShelf || isExotic) && { weight: selectedWeight }),
                quantity: 1,
                countInStock: product.countInStock,
                totalPrice: itemPrice,
                brand: product.brand,
                category: product.category
            };

            addToCart(cartItem);

            // Show success feedback
            setRecentlyAdded(prev => [...prev, {
                ...cartItem,
                timestamp: Date.now()
            }]);

            // Remove success feedback after 3 seconds
            setTimeout(() => {
                setRecentlyAdded(prev => prev.filter(item => item.timestamp !== cartItem.timestamp));
            }, 3000);
        }
    };

    const openImageExpanded = (product) => {
        setExpandedImage(product);
    };

    const closeImageExpanded = () => {
        setExpandedImage(null);
    };

    // Manual close for recently added feedback
    const closeRecentlyAdded = (timestamp) => {
        setRecentlyAdded(prev => prev.filter(item => item.timestamp !== timestamp));
    };

    // Close all recently added items
    const closeAllRecentlyAdded = () => {
        setRecentlyAdded([]);
    };

    const getWeightLabel = (weight) => {
        const weightLabels = {
            ounce: 'Ounce (28g)',
            half: 'Half (14g)',
            quarter: 'Quarter (7g)',
            eighth: 'Eighth (3.5g)'
        };
        return weightLabels[weight] || weight;
    };

    // Get weight options for a product - FIXED VERSION
    const getWeightOptions = (product) => {
        const isFlower = product.category === 'flower';
        const isTopShelf = product.category === 'TopShelf flower';
        const isExotic = product.category === 'exotic';

        if (!isFlower && !isTopShelf && !isExotic) return null;

        // Calculate price options based on category
        let calculatedPriceOptions;
        if (isTopShelf) {
            calculatedPriceOptions = {
                ounce: product.price,
                half: 70,
                quarter: 50,
                eighth: 40
            };
        } else if (isExotic) {
            calculatedPriceOptions = {
                ounce: product.price,
                half: 100,
                quarter: 70,
                eighth: 50
            };
        } else {
            // Regular flower
            calculatedPriceOptions = {
                ounce: product.price,
                half: product.price * 0.6,
                quarter: product.price * 0.4,
                eighth: product.price * 0.3
            };
        }

        return [
            { value: 'ounce', label: `Ounce - $${calculatedPriceOptions.ounce.toFixed(2)}`, grams: '28g' },
            { value: 'half', label: `Half - $${calculatedPriceOptions.half.toFixed(2)}`, grams: '14g' },
            { value: 'quarter', label: `Quarter - $${calculatedPriceOptions.quarter.toFixed(2)}`, grams: '7g' },
            { value: 'eighth', label: `Eighth - $${calculatedPriceOptions.eighth.toFixed(2)}`, grams: '3.5g' }
        ];
    };

    // Get stock badge text and class based on product category
    const getStockBadgeInfo = (product) => {
        const isTopShelf = product.category === 'TopShelf flower';
        const isExotic = product.category === 'exotic';

        if (isTopShelf) {
            return {
                text: '⭐ Premium',
                class: 'premium-stock'
            };
        }
        
        if (isExotic) {
            return {
                text: '🔥 Exotic',
                class: 'exotic-stock'
            };
        }

        return {
            text: product.countInStock > 0 ? 'In Stock' : 'Out of Stock',
            class: product.countInStock > 0 ? 'in-stock' : 'out-of-stock'
        };
    };

    return (
        <>
            <div className="product-list" id="products-section">
                {/* Recently Added Items Feedback - Popup Style */}
                {recentlyAdded.length > 0 && (
                    <div className="recently-added-popup">
                        <div className="popup-content">
                            <div className="popup-header">
                                <h4>Added to Cart! 🎉</h4>
                                <button
                                    className="popup-close"
                                    onClick={closeAllRecentlyAdded}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="popup-items">
                                {recentlyAdded.map(item => (
                                    <div key={item.timestamp} className="popup-item">
                                        <div className="popup-item-image">
                                            <img src={item.image} alt={item.name} />
                                        </div>
                                        <div className="popup-item-details">
                                            <span className="popup-item-name">{item.name}</span>
                                            {item.weight && (
                                                <span className="popup-item-weight">{getWeightLabel(item.weight)}</span>
                                            )}
                                            <span className="popup-item-price">${item.totalPrice.toFixed(2)}</span>
                                        </div>
                                        <button
                                            className="popup-item-close"
                                            onClick={() => closeRecentlyAdded(item.timestamp)}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="popup-actions">
                                <Link to="/cart" className="view-cart-btn">
                                    View Cart ({recentlyAdded.length})
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                <div className="products-grid">
                    {products.map((product) => {
                        const isFlower = product.category === 'flower';
                        const isTopShelf = product.category === 'TopShelf flower';
                        const isExotic = product.category === 'exotic';
                        const hasWeightOptions = isFlower || isTopShelf || isExotic;
                        const selectedWeight = selectedWeights[product.id] || 'ounce';
                        const weightOptions = getWeightOptions(product);
                        const stockBadgeInfo = getStockBadgeInfo(product);

                        // FIXED: Use different variable name to avoid conflicts
                        const productPriceOptions = isTopShelf ? {
                            ounce: product.price,
                            half: 70,
                            quarter: 50,
                            eighth: 40
                        } : isExotic ? {
                            ounce: product.price,
                            half: 100,
                            quarter: 70,
                            eighth: 50
                        } : isFlower ? {
                            ounce: product.price,
                            half: product.price * 0.6,
                            quarter: product.price * 0.4,
                            eighth: product.price * 0.3
                        } : { default: product.price };

                        const itemPrice = hasWeightOptions ? productPriceOptions[selectedWeight] : product.price;

                        // Debug logging for exotic products
                        if (isExotic) {
                            console.log('Exotic Product Debug:', {
                                name: product.name,
                                category: product.category,
                                hasWeightOptions,
                                weightOptions,
                                selectedWeight,
                                itemPrice
                            });
                        }

                        return (
                            <div key={product.id} className="product-card">
                                <div className="card-header">
                                    <div className="image-container">
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            className="product-image"
                                            onClick={() => openImageExpanded(product)}
                                        />
                                        <div className="image-overlay">
                                            <button
                                                className="expand-btn"
                                                onClick={() => openImageExpanded(product)}
                                                aria-label="Expand image"
                                            >
                                                <FaExpand />
                                            </button>
                                            <Link
                                                to={`/product/${product.id}`}
                                                className="view-details-btn"
                                            >
                                                <FaEye />
                                                View Details
                                            </Link>
                                        </div>
                                        
                                        {/* Multiple Badges */}
                                        <div className="badges-container">
                                            {/* Stock Badge */}
                                            <div className={`stock-badge ${stockBadgeInfo.class}`}>
                                                <span className="badge-text">{stockBadgeInfo.text}</span>
                                            </div>
                                            
                                            {/* Exotic Badge */}
                                            {isExotic && (
                                                <div className="exotic-badge">
                                                    <FaStar className="exotic-icon" />
                                                    <span className="exotic-text">Exotic</span>
                                                </div>
                                            )}
                                            
                                            {/* Top Shelf Badge */}
                                            {isTopShelf && (
                                                <div className="top-shelf-badge">
                                                    <FaStar className="top-shelf-icon" />
                                                    <span className="top-shelf-text">Top Shelf</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="product-meta">
                                        <h3 className="product-name">{product.name}</h3>
                                        <div className="product-brand">{product.brand}</div>
                                        <div className="product-rating">
                                            {'★'.repeat(Math.floor(product.rating))}{'☆'.repeat(5 - Math.floor(product.rating))}
                                            <span className="rating-count">({product.numReviews})</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-body">
                                    {hasWeightOptions && weightOptions && (
                                        <div className="weight-selector">
                                            <label className="selector-label">
                                                <FaWeightHanging />
                                                Select Weight
                                            </label>
                                            <div className="custom-select">
                                                <select
                                                    value={selectedWeight}
                                                    onChange={(e) => handleWeightChange(product.id, e.target.value)}
                                                    className="weight-dropdown"
                                                >
                                                    {weightOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="select-arrow">▼</div>
                                            </div>
                                            <div className="weight-grams">
                                                {weightOptions.find(opt => opt.value === selectedWeight)?.grams}
                                            </div>
                                        </div>
                                    )}

                                    <div className="price-section">
                                        <div className="price-main">${itemPrice.toFixed(2)}</div>
                                        <div className="category-tag">
                                            <span className={`category-text ${isTopShelf ? 'top-shelf-category' : isExotic ? 'exotic-category' : ''}`}>
                                                {isTopShelf ? 'Top Shelf' : isExotic ? 'Exotic' : product.category}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-footer">
                                    <button
                                        onClick={() => handleAddToCart(product)}
                                        className={`add-to-cart-btn ${product.countInStock <= 0 ? 'out-of-stock' : ''} ${isTopShelf ? 'premium-btn' : isExotic ? 'exotic-btn' : ''}`}
                                        disabled={product.countInStock <= 0}
                                    >
                                        {product.countInStock > 0 ? (
                                            <>
                                                <span className="btn-text">Add to Cart</span>
                                                <span className="btn-price">${itemPrice.toFixed(2)}</span>
                                            </>
                                        ) : (
                                            <span className="out-of-stock-text">Out of Stock</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Scroll to Top Button */}
            {showScrollTop && (
                <button
                    className="scroll-top-btn"
                    onClick={scrollToTop}
                    aria-label="Scroll to top"
                >
                    <FaArrowUp />
                </button>
            )}

            {/* Expanded Image Modal */}
            {expandedImage && (
                <div className="image-expanded-modal" onClick={closeImageExpanded}>
                    <div className="modal-overlay" onClick={closeImageExpanded}></div>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={closeImageExpanded}>
                            ×
                        </button>
                        <img
                            src={expandedImage.image}
                            alt={expandedImage.name}
                            className="expanded-image"
                        />
                        <div className="modal-product-info">
                            <h3>{expandedImage.name}</h3>
                            <p className="modal-brand">{expandedImage.brand}</p>
                            <div className="modal-badges">
                                {expandedImage.category === 'TopShelf flower' && (
                                    <span className="modal-badge top-shelf-badge">
                                        <FaStar /> Top Shelf
                                    </span>
                                )}
                                {expandedImage.category === 'exotic' && (
                                    <span className="modal-badge exotic-badge">
                                        <FaStar /> Exotic
                                    </span>
                                )}
                            </div>
                            <p className={`modal-category ${expandedImage.category === 'TopShelf flower' ? 'top-shelf-category' : expandedImage.category === 'exotic' ? 'exotic-category' : ''}`}>
                                {expandedImage.category === 'TopShelf flower' ? 'Top Shelf' : expandedImage.category === 'exotic' ? 'Exotic' : expandedImage.category}
                            </p>
                            {(expandedImage.category === 'flower' || expandedImage.category === 'TopShelf flower' || expandedImage.category === 'exotic') && (
                                <div className="modal-weight-info">
                                    <strong>Available Weights:</strong>
                                    <div className="weight-options-modal">
                                        {getWeightOptions(expandedImage)?.map(option => (
                                            <div key={option.value} className="weight-option-modal">
                                                {option.label} ({option.grams})
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <p className="modal-price">From ${expandedImage.price}</p>
                            <Link
                                to={`/product/${expandedImage.id}`}
                                className="modal-details-btn"
                                onClick={closeImageExpanded}
                            >
                                <FaEye />
                                View Full Details & Purchase
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProductList;